<script>
  import Modal from "@components/shared/modal.svelte"
  import FormSection from "@components/shared/form-section.svelte"
  import CountrySelect from "@components/shared/country-select.svelte"
  import OrganisationFields from "@components/organisations/organisation-fields.svelte"
  import PhoneInput from "@components/shared/phone-input.svelte";
  import EmailInput from "@components/shared/email-input.svelte";
  import SegmentedControl from "@components/shared/segmented-control.svelte";
  import { fly } from "svelte/transition"
  import pick from 'object.pick'
  import isEqualObject from "is-equal-shallow"
  import { get, post } from '@lib/requests'
  import debounce from "debounce"
  import { delay, capitalize, showAlert, commonFrenchFirstNames } from "@lib/utils";
  import useFormData from "@hooks/use-form-data.svelte.js"

  let { open = $bindable(false), onCreate, client = $bindable(null) } = $props()

  let formType = $derived(client ? "edit" : "create")

  let clientType = $state("individual")
  let nameLabel = $derived(clientType === "individual" ? "Nom *" : "Nom de la personne représentante *")
  let nameInput = $state(null)
  let organisationLookup = $state(null)
  let street1Input = $state(null)

  let defaultClinicId = $state(document.querySelector("[data-user]")?.dataset?.user ? JSON.parse(document.querySelector("[data-user]").dataset.user)?.clinic_id : "")
  let clinics = $state(JSON.parse(document.querySelector("[data-clinics]").dataset.clinics))
  let clinic = $derived(clinics.find(clinic => clinic.id === form.data.clinic_id))
  let vets = $derived(clinic?.vets || [])
  let similarClients = $state([])
  let nameInputFocused = $state(false)
  let showSimilarClients = $state(true)
  
  let submitting = $state(false)

  const organisationData = {
    name: "",
    type: "",
    siren: "",
    siret: "",
    tax_number: "",
    tax_scheme: "standard",
    ede: "",
    breeder: false,
    contracted: false
  }

  const addressData = {
    street_1: "",
    street_2: "",
    postcode: "",
    town: "",
    country: "FR"
  } 

  const form = useFormData({
    type: "individual",
    name: "",
    address: addressData,
    primary_phone: "",
    secondary_phone: "",
    primary_email: "",
    secondary_email: "",
    organisation: organisationData,
    accepts_email: true,
    accepts_sms: true,
    accepts_post: true,
    invoice_reminders_enabled: true,
    rgpd: 0,
    consultations_allowed: 1,
    clinic_id: defaultClinicId,
    vet_id: "",
    act_discount: 0,
    product_discount: 0
  })

  let valid = $derived(!!form.data.name)
  let emptyOrganisation = $derived(isEqualObject(form.data.organisation, organisationData))
  let emptyAddress = $derived(isEqualObject(form.data.address, addressData))

  async function checkSimilarNames() {
    similarClients = []
    try {
      const response = await get(`/api/v1/clients/similar?name=${form.data.name}`)
      const json = await response.json
      similarClients = json
    } catch (error) {
      console.error(error)
    }
  }
  
  const debouncedCheckSimilarNames = debounce(checkSimilarNames, 500)

  function handleNameInput(e) {
    if (formType === "edit") return
    if (form.data.name.length < 5) return similarClients = []
    const words = form.data.name.split(" ").filter(Boolean)
    if (words.length === 1 && form.data.name.length < 5) return similarClients = []
    if (words.length > 1 && words.some(word => word.length < 3)) return similarClients = []
    if (words.length === 1 && commonFrenchFirstNames.includes(words[0].toLowerCase())) return similarClients = []
    
    debouncedCheckSimilarNames()
  }

  function handleNameFocus() {
    nameInputFocused = true
  }

  function handleNameBlur() {
    nameInputFocused = false
  }

  function handleSimilarClientSelect(client) {
    similarClients = []
    onCreate?.(client)
  }

  async function closeSimilarClients() {
    showSimilarClients = false
    await delay(20)
    nameInput?.focus()
  }

  async function handleClientTypeChange() {
    if (clientType === "organisation") {
      if (!form.data.organisation.name) {
        organisationLookup?.focusLookup()
      } else {
        organisationLookup?.scrollIntoView()
      }
    } else {
      if (form.data.name && form.data.address.street_1) return
      await delay(20)
      if (form.data.name) {
        street1Input?.focus()
      } else {
        nameInput?.focus()
      }
    }
  }

  function handleOrganisationSelect(organisation) {
    const addressNotSet = !form.data.address.street_1 && !form.data.address.street_2 && !form.data.address.postcode && !form.data.address.town
    if (addressNotSet) {
      let address = pick(organisation, ["street_1", "street_2", "postcode", "town"])
      Object.keys(address).forEach(key => {
        address[key] = capitalize(address[key])
      })
      form.data.address = Object.assign({}, form.data.address, address)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    submitting = true
    if (clientType === "individual") form.data.organisation = organisationData
    const { type, organisation, address, primary_phone, secondary_phone, primary_email, secondary_email, ...rest } = form.data
    let client = { ...rest }
    if (!emptyOrganisation) client.organisation_attributes = organisation
    if (!emptyAddress) client.address_attributes = address
    if (primary_phone) client.primary_phone_attributes = { number: primary_phone }
    if (secondary_phone) client.secondary_phone_attributes = { number: secondary_phone }
    if (primary_email) client.primary_email_attributes = { address: primary_email }
    if (secondary_email) client.secondary_email_attributes = { address: secondary_email }
    try {
      const response = await post(`/api/v1/clients`, { body: { client } })
      const json = await response.json
      if (response.ok) {
        showAlert("Client créé avec succès", { type: "success" })
        onCreate?.(json)
        handleClose()
      } else {
        console.error("Erreur lors de la création du client", json)
        const errorMessage = Object.entries(json.errors).map(([key, value]) => `${key}: ${value}`).join("\n")
        showAlert(errorMessage, { type: "error" })
      }
    } catch (error) {
      console.error(error)
    } finally {
      submitting = false
    }
  }

  function handleClose() {
    open = false
    form.reset()
  }

  $effect(() => {
    if (open) { 
      nameInput?.focus() 
    }
  })
  
</script>

<Modal bind:open title={formType === "edit" ? "Modifier client" : "Nouveau client"} closeOnEscape={false} onClose={handleClose} width="50rem">

  <form id="client-form" onsubmit={handleSubmit}>

    <div class="form-items">

      <div class="form-item col-span-4">
        <label for="type">Type</label>
        <select name="type" id="type" class="select" bind:value={clientType} onchange={handleClientTypeChange}>
          <option value="individual">Individuel</option>
          <option value="organisation">Organisation</option>
        </select>
      </div>

      <!-- Name -->
      <div class="form-item col-span-8">
        <label for="name">{nameLabel}</label>
        <input type="text" name="name" id="name" bind:value={form.data.name} required bind:this={nameInput} oninput={handleNameInput} onfocus={handleNameFocus} onblur={handleNameBlur} />
      </div>

      {#if formType === "create" && similarClients.length > 0 && nameInputFocused && showSimilarClients}
        <div class="form-item col-span-12" in:fly={{ y: -10, duration: 200 }}>

          <div class="flex justify-between items-center mb-1">
            <label for="similar_clients">Est-ce que vous vouliez dire ?</label>
            <button type="button" class="text-xs text-gray-500 hover:text-gray-700" onpointerdown={closeSimilarClients}>Fermer</button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" id="similar-clients">
            {#each similarClients as client (client.id)}
              <button type="button" class="p-5 bg-yellow-100/80 shadow-lg border-2 border-yellow-200 rounded relative hover:no-underline cursor-pointer" onpointerdown={() => handleSimilarClientSelect(client)} tabindex="-1">
                <h5 class="text-smaller">{client.name}</h5>
                <p class="text-gray-500 text-xs">{client.email}</p>
                <p class="text-gray-500 text-xs">{client.phone}</p>
              </button>
            {/each}
          </div>
          
        </div>
      {/if}

      <!-- Contact details -->
      <FormSection label="Contact">

        <div class="form-item col-span-6">
          <label for="primary_phone">Téléphone principal</label>
          <PhoneInput bind:value={form.data.primary_phone} />
        </div>

        <div class="form-item col-span-6">
          <label for="secondary_phone">Téléphone secondaire</label>
          <PhoneInput bind:value={form.data.secondary_phone} />
        </div>

        <div class="form-item col-span-6">
          <label for="primary_email">Email principal</label>
          <EmailInput name="primary_email" id="primary_email" bind:value={form.data.primary_email} />
        </div>

        <div class="form-item col-span-6">
          <label for="secondary_email">Email secondaire</label>
          <EmailInput name="secondary_email" id="secondary_email" bind:value={form.data.secondary_email} />
        </div>
        
      </FormSection>

      {#if clientType === "organisation"}
        <OrganisationFields bind:data={form.data.organisation} bind:this={organisationLookup} onSelect={handleOrganisationSelect} />
      {/if}

      <!-- Address -->
      <FormSection label="Adresse">

        <div class="form-item col-span-6">
          <label for="address_street_1">Adresse 1</label>
          <input type="text" name="address_street_1" id="address_street_1" bind:value={form.data.address.street_1} bind:this={street1Input}>
        </div>

        <div class="form-item col-span-6">
          <label for="address_street_2">Adresse 2</label>
          <input type="text" name="address_street_2" id="address_street_2" bind:value={form.data.address.street_2}>
        </div>

        <div class="form-item col-span-6">
          <label for="address_postcode">Code postal</label>
          <input type="text" name="address_postcode" id="address_postcode" bind:value={form.data.address.postcode}>
        </div>

        <div class="form-item col-span-6">
          <label for="address_town">Ville</label>
          <input type="text" name="address_town" id="address_town" bind:value={form.data.address.town}>
        </div>

        <CountrySelect bind:value={form.data.address.country} />
        
      </FormSection>

      <FormSection label="Communication">

        <div class="col-span-12 flex items-center flex-wrap gap-6">

          <label for="accepts_email" class="flex items-center gap-3">
            <input class="input !p-0" type="checkbox" name="accepts_email" id="accepts_email" bind:checked={form.data.accepts_email}>
            <span>Accepte les emails</span>
          </label>
    
          <label for="accepts_sms" class="flex items-center gap-3">
            <input class="input !p-0" type="checkbox" name="accepts_sms" id="accepts_sms" bind:checked={form.data.accepts_sms}>
            <span>Accepte les SMS</span>
          </label>

          <label for="accepts_post" class="flex items-center gap-3">
            <input class="input !p-0" type="checkbox" name="accepts_post" id="accepts_post" bind:checked={form.data.accepts_post}>
            <span>Accepte les courriers</span>
          </label>
    
        </div>

      </FormSection>

      <FormSection label="Accord RGPD">

      <div class="form-item col-span-6">
        <label for="rgpd">Accord RGPD donné?</label>
        <SegmentedControl bind:value={form.data.rgpd} options={[{ label: "Non", value: 0 }, { label: "Oui", value: 1 }]} />
      </div>

      <div class="form-item col-span-6">
        <label for="consultations_allowed">Consultations autorisées?</label>
        <SegmentedControl bind:value={form.data.consultations_allowed} options={[{ label: "Non", value: 0 }, { label: "Oui", value: 1 }]} />
      </div>

      <div class="form-item col-span-6">
        <label for="invoice_reminders_enabled">Relances de factures automatiques ?</label>
        <SegmentedControl bind:value={form.data.invoice_reminders_enabled} options={[{ label: "Non", value: false }, { label: "Oui", value: true }]} />
      </div>

      </FormSection>

      <FormSection label="Préférences">

        <div class="form-item col-span-6">
          <label for="clinic_id">Clinique</label>
          <select bind:value={form.data.clinic_id} class="select">
            <option value=""></option>
            {#each clinics as clinic}
              <option value={clinic.id}>{clinic.name}</option>
            {/each}
          </select>
        </div>

        <div class="form-item col-span-6">
          <label for="vet_id">Vétérinaire</label>
          <select bind:value={form.data.vet_id} class="select">
            <option value=""></option>
            {#each vets as vet}
              <option value={vet.id}>{vet.name}</option>
            {/each}
          </select>
        </div>
        
      </FormSection>

      <FormSection label="Remises">

        <!-- Fields for act and product discount 6 cols each -->
        <div class="form-item col-span-6">
          <label for="act_discount">Remise acte (%)</label>
          <input type="number" name="act_discount" id="act_discount" bind:value={form.data.act_discount} class="input" min="0" max="100">
        </div>

        <div class="form-item col-span-6">
          <label for="product_discount">Remise produit (%)</label>
          <input type="number" name="product_discount" id="product_discount" bind:value={form.data.product_discount} class="input" min="0" max="100">
        </div>
      </FormSection>

    </div>
    
  </form>

  {#snippet footerButtons()}
    <button class="button submit" type="submit" disabled={!valid || submitting} form="client-form">Valider</button>
  {/snippet}

</Modal>